import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import Table from "./table";
import { NavErrorBanner, NavLoading } from "./status";
import { coverageSummaryColumns, syncStateColumns } from "./list_of_columns";
import { groupDigits } from "./cell_formatter";
import { NAV_PATHS, describeError, useNavRequest } from "./api";
import messages from "./messages";

const SYNC_STATE_TABLE_MIN_WIDTH = 1310;
const COVERAGE_TABLE_MIN_WIDTH = 1330;

/**
 * "Did the backfill cover every fund?"
 *
 * Three reads answer it: funds_sync_state (what each job did last), the per-fund coverage summary
 * (row count and the nav_date range the sync produced), and the fund master. The master is what
 * turns a silence into a finding - the coverage summary is built from funds_nav_history, so a fund
 * with no NAV row at all is simply absent from it. Diffing the two names that fund instead of
 * leaving an operator to notice that 68 rows were listed where 69 were expected.
 */
function SyncStatusTab() {
  const intl = useIntl();
  const requestNav = useNavRequest();

  const syncColumns = useMemo(() => syncStateColumns(intl), [intl]);
  const coverageColumns = useMemo(() => coverageSummaryColumns(intl), [intl]);

  const [syncStateRows, setSyncStateRows] = useState([]);
  const [summaryRows, setSummaryRows] = useState([]);
  const [masterFunds, setMasterFunds] = useState(null);

  const [requesting, setRequesting] = useState(false);
  const [syncStateError, setSyncStateError] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [masterError, setMasterError] = useState(null);

  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchSyncStatus = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setRequesting(true);
    setSyncStateError(null);
    setSummaryError(null);
    setMasterError(null);

    const isCurrent = () => mountedRef.current && requestIdRef.current === requestId;

    const syncStatePromise = requestNav(NAV_PATHS.syncState)
      .then(result => {
        if (!isCurrent() || result.signedOut) {
          return;
        }
        setSyncStateRows(result.data);
      })
      .catch(error => {
        if (!isCurrent()) {
          return;
        }
        setSyncStateRows([]);
        setSyncStateError(describeError(error, intl, messages));
      });

    const summaryPromise = requestNav(NAV_PATHS.historySummary)
      .then(result => {
        if (!isCurrent() || result.signedOut) {
          return;
        }
        setSummaryRows(result.data);
      })
      .catch(error => {
        if (!isCurrent()) {
          return;
        }
        setSummaryRows([]);
        setSummaryError(describeError(error, intl, messages));
      });

    // The fund master is the only way to name a fund that has no NAV history at all. Called with
    // no page parameter it returns every fund.
    const masterPromise = requestNav(NAV_PATHS.fundMaster)
      .then(result => {
        if (!isCurrent() || result.signedOut) {
          return;
        }
        setMasterFunds(result.data);
      })
      .catch(error => {
        if (!isCurrent()) {
          return;
        }
        setMasterFunds(null);
        setMasterError(describeError(error, intl, messages));
      });

    await Promise.all([syncStatePromise, summaryPromise, masterPromise]);

    if (isCurrent()) {
      setRequesting(false);
    }
  }, [requestNav, intl]);

  useEffect(() => {
    fetchSyncStatus();
  }, [fetchSyncStatus]);

  const coveredFundNos = useMemo(() => {
    const covered = {};
    summaryRows.forEach(row => {
      covered[Number(row.fund_no)] = true;
    });
    return covered;
  }, [summaryRows]);

  const missingFunds = useMemo(() => {
    if (!masterFunds) {
      return [];
    }
    return masterFunds.filter(fund => !coveredFundNos[Number(fund.fund_no)]);
  }, [masterFunds, coveredFundNos]);

  const totalNavRows = useMemo(
    () => summaryRows.reduce((sum, row) => sum + (Number(row.row_count) || 0), 0),
    [summaryRows]
  );

  const renderCoverageBanner = () => {
    if (masterError) {
      return (
        <div className="nav-coverage-banner nav-coverage-banner-unknown">
          <p className="nav-coverage-banner-header">
            {intl.formatMessage(messages.sectionMissingFunds)}
          </p>
          <p className="nav-coverage-banner-body">
            {intl.formatMessage(messages.missingFundsUnknown)}
          </p>
        </div>
      );
    }

    if (!masterFunds) {
      return null;
    }

    if (missingFunds.length === 0) {
      return (
        <div className="nav-coverage-banner nav-coverage-banner-ok">
          <p className="nav-coverage-banner-header">
            {intl.formatMessage(messages.sectionMissingFunds)}
          </p>
          <p className="nav-coverage-banner-body">
            {intl.formatMessage(messages.missingFundsNone, {
              total: masterFunds.length
            })}
          </p>
        </div>
      );
    }

    return (
      <div className="nav-coverage-banner nav-coverage-banner-missing">
        <p className="nav-coverage-banner-header">
          {intl.formatMessage(messages.sectionMissingFunds)}
        </p>
        <p className="nav-coverage-banner-body">
          {intl.formatMessage(messages.missingFundsBody, {
            count: missingFunds.length,
            total: masterFunds.length
          })}
        </p>
        <div className="nav-coverage-missing-list">
          {missingFunds.map(fund => (
            <span className="nav-coverage-missing-chip" key={`missing-${fund.fund_no}`}>
              {fund.fund_no} - {fund.fund_code}
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      {requesting ? (
        <NavLoading text={intl.formatMessage(messages.loadingSyncStatus)} />
      ) : null}

      <div className="nav-filter-bar">
        <div className="nav-filter-actions">
          <button className="nav-search-button" onClick={() => fetchSyncStatus()}>
            {intl.formatMessage(messages.refresh)}
          </button>
        </div>
      </div>

      <NavErrorBanner message={syncStateError} onRetry={fetchSyncStatus} />

      <p className="nav-section-title">
        {intl.formatMessage(messages.sectionSyncJobs)}
      </p>
      <Table
        columns={syncColumns}
        data={syncStateRows}
        errorMessage={syncStateError}
        onRetry={fetchSyncStatus}
        tableMinWidth={SYNC_STATE_TABLE_MIN_WIDTH}
      />

      {renderCoverageBanner()}

      <NavErrorBanner message={summaryError} onRetry={fetchSyncStatus} />

      <p className="nav-section-title">
        {intl.formatMessage(messages.sectionCoverage)}
      </p>
      <p className="nav-total-rows">
        {intl.formatMessage(messages.coverageSummaryCount, {
          funds: groupDigits(summaryRows.length),
          rows: groupDigits(totalNavRows)
        })}
      </p>
      <Table
        columns={coverageColumns}
        data={summaryRows}
        errorMessage={summaryError}
        onRetry={fetchSyncStatus}
        tableMinWidth={COVERAGE_TABLE_MIN_WIDTH}
      />
    </React.Fragment>
  );
}

export default SyncStatusTab;
